export const COMPLETION_EVIDENCE_PREFIX = '[ACM_COMPLETION_EVIDENCE]';

export type CompletionEvidenceTranslator = (
  key: string,
  fallback: string,
  values?: Record<string, string>,
) => string;

const fallbackTranslate: CompletionEvidenceTranslator = (_key, fallback, values) =>
  fallback.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => values?.[name] ?? '');

export type CompletionEvidenceGap = 'missing_write';

export interface CompletionEvidenceInput {
  userText: string;
  assistantText: string;
  successfulTools: string[];
  autonomyMode: 'ask' | 'workspace';
  autoCommit: boolean;
  hasInteractiveCard: boolean;
  evidenceRetry: boolean;
}

export interface CompletePatchBlock {
  full: string;
  path: string;
  content: string;
  index: number;
}

/** Parse only CLOSED full-file patch blocks. An output-truncated block is never actionable. */
export function completePatchBlocks(text: string): CompletePatchBlock[] {
  const re = /```patch[ \t]+([^\n`]+)[ \t]*\n([\s\S]*?)\n?```/g;
  const blocks: CompletePatchBlock[] = [];
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const path = match[1]?.trim();
    if (!path) continue;
    blocks.push({
      full: match[0],
      path,
      content: match[2] ?? '',
      index: match.index,
    });
  }
  return blocks;
}

export function incompletePatchStart(text: string): number {
  return text.search(/```patch(?:[ \t]+[^\n`]*)?(?:\n|$)/i);
}

function canonicalToolName(name: string): string {
  return name.trim().toLowerCase().replaceAll('.', '_');
}

const MUTATION_INTENT =
  /\b(přidej|pridej|přidat|pridat|doplň|dopln|doplnit|uprav|upravit|změň|zmen|změnit|zmenit|oprav|opravit|implementuj|implementovat|vytvoř|vytvor|vytvořit|vytvorit|odstraň|odstran|odstranit|předělej|predelej|předělat|predelat|udělej|udelej|udělat|udelat|dodělej|dodelej|dodělat|dodelat|dokonči|dokončit|pokračuj|pokračovat|add|change|update|fix|implement|create|remove|redesign|build|finish|continue)\b/i;

const COMPLETION_CLAIM =
  /\b(hotovo|dokončeno|dokonceno|implementoval(?:a|i)? jsem|přidal(?:a|i)? jsem|pridal(?:a|i)? jsem|upravil(?:a|i)? jsem|změnil(?:a|i)? jsem|zmenil(?:a|i)? jsem|opravil(?:a|i)? jsem|vytvořil(?:a|i)? jsem|vytvoril(?:a|i)? jsem|i (?:have )?(?:implemented|added|updated|fixed|created)|it's done|all done)\b/i;

const PROJECT_WRITE_TOOLS = new Set([
  'codable_workspace_write',
  'codable_workspace_replace',
  'codable_god_write',
]);

/**
 * Live: text „hotovo“ bez workspace zápisu není dokončení.
 * Úspěšný replace/write stačí. Git se nenutí. Chybějící zápis jde agentovi,
 * ne uživateli jako stop hláška. Patch karta v Live práci neschovává.
 */
export function completionEvidenceGap(
  input: CompletionEvidenceInput,
): CompletionEvidenceGap | null {
  if (input.autonomyMode !== 'workspace') return null;
  const mutationRequested = MUTATION_INTENT.test(input.userText);
  const completionClaimed = COMPLETION_CLAIM.test(input.assistantText);
  if (!mutationRequested || (!input.evidenceRetry && !completionClaimed)) {
    return null;
  }

  const tools = input.successfulTools.map(canonicalToolName);
  if (tools.some((name) => PROJECT_WRITE_TOOLS.has(name))) return null;
  return 'missing_write';
}

export function completionEvidenceFollowup(
  gap: CompletionEvidenceGap,
  originalRequest: string,
  successfulTools: string[],
  requireCommit: boolean,
  translate: CompletionEvidenceTranslator = fallbackTranslate,
): string {
  const evidence = successfulTools.map(canonicalToolName).join(',') || 'none';
  void gap;
  void requireCommit;
  const missing = translate(
    'codableChat.evidenceMissingWrite',
    'chybí skutečný zápis do workspace',
  );
  return `${COMPLETION_EVIDENCE_PREFIX} retry=1 evidence=${evidence}\n` +
    translate(
      'codableChat.evidenceFollowup',
      'Úpravu jsi popsal, ale v tomto tahu neproběhl zápis do DEV workspace. Teď proveď změnu nativně: read → workspace_replace (malá úprava) nebo workspace_write (nový/celý soubor). Git commit neřeš, dokud zápis neprojde.\nPůvodní požadavek: {{request}}',
      {
        missing,
        request: originalRequest.slice(0, 1_500),
      },
    );
}

export function priorCompletionEvidence(text: string): string[] {
  if (!text.startsWith(COMPLETION_EVIDENCE_PREFIX)) return [];
  const match = text.split('\n', 1)[0]?.match(/\bevidence=([^\s]+)/);
  const evidence = match?.[1];
  if (!evidence || evidence === 'none') return [];
  return evidence.split(',').filter(Boolean);
}
